import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Background from '../../../assets/background-desktop.jpg';
import { loadMyBankDetailsAsHealer } from '../../../core/usecases/healer-bank-details/load-healer-bank-details';
import { updateHealerBankDetails } from '../../../core/usecases/healer-bank-details/update-healer-bank-details';
import { showNavbar } from '../../../store/selectors/global.selector';
import { selectHealerBankDetailsPresentation, updateHealerBankDetailsTab } from '../../../store/selectors/healer-bank-details.selector';
import { hideLoader, showLoader } from '../../../store/selectors/loading-spinner-selector';
import BankDetailsFields from './bank-details-fields';
import NoBankDetails from './no-bank-details-screen';

const BankDetailsContainer = () => {
  const [email, setEmail] = useState<string | undefined>(undefined);
  const [type, setType] = useState<string | undefined>(undefined);
  const [name, setName] = useState<string | undefined>(undefined);
  const [surname, setSurname] = useState<string | undefined>(undefined);
  const [achRoutingNumber, setAchRoutingNumber] = useState<string | undefined>(undefined);
  const [accountNumber, setAccountNumber] = useState<string | undefined>(undefined);
  const [accountType, setAccountType] = useState<string | undefined>(undefined);
  const [swiftCode, setSwiftCode] = useState<string | undefined>(undefined);
  const [ibanCode, setIbanCode] = useState<string | undefined>(undefined);
  const [country, setCountry] = useState<string | undefined>(undefined);
  const [city, setCity] = useState<string | undefined>(undefined);
  const [address, setAddress] = useState<string | undefined>(undefined);
  const [postCode, setPostCode] = useState<string | undefined>(undefined);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [accountNumberValidationMessage, setAccountNumberValidationMessage] = useState<string | null>(null);
  const [achRoutingNumberValidationMessage, setAchRoutingNumberValidationMessage] = useState<string | null>(null);
  const [swiftCodeValidationMessage, setSwiftCodeValidationMessage] = useState<string | null>(null);
  const [ibanCodeValidationMessage, setIbanCodeValidationMessage] = useState<string | null>(null);
  const [showModify, setShowModify] = useState<boolean>(false);

  const dispatch = useDispatch();
  const data = useSelector(selectHealerBankDetailsPresentation);

  useEffect(() => {
    data.showLoader ? dispatch(showLoader()) : dispatch(hideLoader());
  }, [dispatch, data]);

  useEffect(() => {
    dispatch(showNavbar('HEALER_SIDE_NAVBAR'));
  }, [dispatch]);

  useEffect(() => {
    // dispatch(showLoader())
    dispatch<any>(loadMyBankDetailsAsHealer());
  }, [dispatch]);

  useEffect(() => {
    setEmail(data.email);
    setType(data.type);
    setName(data.name);
    setSurname(data.surname);
    setAchRoutingNumber(data.achRoutingNumber);
    setAccountNumber(data.accountNumber);
    setAccountType(data.accountType);
    setSwiftCode(data.swiftCode);
    setIbanCode(data.ibanCode);
    setCountry(data.country);
    setCity(data.city);
    setAddress(data.address);
    setPostCode(data.postCode);
  }, [data]);

  const handleEmailInput = (event: any) => {
    setEmail(event.target.value);
  };

  const handleTabChange = (tabValue: string) => {
    dispatch(updateHealerBankDetailsTab(tabValue));
    setType(tabValue);
  };

  const handleNameInput = (event: any) => {
    setName(event.target.value);
  };

  const handleSurnameInput = (event: any) => {
    setSurname(event.target.value);
  };

  const handleAchRoutingNumber = (event: any) => {
    setAchRoutingNumber(event.target.value);
    if (event.target.value.trim() === '') {
      setAchRoutingNumberValidationMessage('Filled must be filled');
    } else {
      setAchRoutingNumberValidationMessage(null);
    }
  };

  const handleAccountNumber = (event: any) => {
    setAccountNumber(event.target.value);
    const value = event.target.value.trim();
    if (value.length < 4) {
      setAccountNumberValidationMessage('Account number is too small. Please enter a valid <br/> account number  between 4-17 digits.');
    } else {
      setAccountNumberValidationMessage(null);
    }
  };

  const handleAccountType = (event: any) => {
    setAccountType(event.target.value);
  };

  const handleSwiftCode = (event: any) => {
    setSwiftCode(event.target.value);
    if (event.target.value.trim() === '') {
      setSwiftCodeValidationMessage('Filled must be filled');
    } else {
      setSwiftCodeValidationMessage(null);
    }
  };

  const handleIbanCode = (event: any) => {
    setIbanCode(event.target.value);
    const value = event.target.value.trim();
    if (value.length < 4) {
      setIbanCodeValidationMessage('Account number is too small. Please enter a valid <br/> account number  between 4-17 digits.');
    } else {
      setIbanCodeValidationMessage(null);
    }
  };

  const handleCountry = (event: any) => {
    setCountry(event.target.value);
  };

  const handleCity = (event: any) => {
    setCity(event.target.value);
  };

  const handleAddress = (event: any) => {
    setAddress(event.target.value);
  };

  const handlePostCode = (event: any) => {
    setPostCode(event.target.value);
  };

  const submitBankDetails = () => {
    dispatch<any>(
      updateHealerBankDetails({
        email,
        type,
        name,
        surname,
        achRoutingNumber: type === 'SWIFT' ? undefined : achRoutingNumber,
        accountNumber: type === 'SWIFT' ? undefined : accountNumber,
        accountType: type === 'SWIFT' ? undefined : accountType,
        swiftCode: type === 'ACH' ? undefined : swiftCode,
        ibanCode: type === 'ACH' ? undefined : ibanCode,
        country,
        city,
        address,
        postCode,
      })
    );
    setShowModify(false);
  };

  useEffect(() => {
    handleTabChange('ACH');
  }, []);

  const handleSetBankDetails = () => {
    setShowBankDetails(true);
    setShowModify(true);
  };

  const handleModifyClick = () => {
    setShowModify(true);
  };

  const handleCancelClick = () => {
    setShowModify(false);
  };

  return (
    <div
      style={{ backgroundImage: `url(${Background})`, backgroundRepeat: 'round', backgroundSize: 'cover', backgroundAttachment: 'fixed' }}
      className="bg-cover w-screen h-full min-h-screen">
      <div className="pt-50px pl-[17vw] pr-24px w-screen">
        <h1 className="font-[GrandSlang] text-primary text-[36px] ml-10">Bank details</h1>
        {!showBankDetails && !data.name && !data.surname ? (
          <NoBankDetails handleSetBankDetailsFn={handleSetBankDetails} />
        ) : (
          <BankDetailsFields
            data={data}
            handleInputEmailFn={handleEmailInput}
            handleTabChangeFn={handleTabChange}
            email={email}
            handleNameInputFn={handleNameInput}
            name={name}
            surname={surname}
            handleSurnameInputFn={handleSurnameInput}
            achRoutingNumber={achRoutingNumber}
            handleAchRoutingNumberFn={handleAchRoutingNumber}
            accountNumber={accountNumber}
            handleAccountNumberFn={handleAccountNumber}
            accountType={accountType}
            handleAccountTypeFn={handleAccountType}
            swiftCode={swiftCode}
            hadnleSwiftCodeFn={handleSwiftCode}
            ibanCode={ibanCode}
            hadnleIbanCodeFn={handleIbanCode}
            country={country}
            handleCountryFn={handleCountry}
            city={city}
            handleCityFn={handleCity}
            address={address}
            handleAddressFn={handleAddress}
            postCode={postCode}
            handlePostCodeFn={handlePostCode}
            submitBankDetailsFn={submitBankDetails}
            accountNumberValidationMessage={accountNumberValidationMessage}
            ibanCodeValidationMessage={ibanCodeValidationMessage}
            achRoutingNumberValidationMessage={achRoutingNumberValidationMessage}
            swiftCodeValidationMessage={swiftCodeValidationMessage}
            showModify={showModify}
            handleModifyClickFn={handleModifyClick}
            handleCancelClickFn={handleCancelClick}
          />
        )}
      </div>
    </div>
  );
};

export default BankDetailsContainer;
